import {
  Checkbox,
  Input,
  Label,
  Radio,
  RadioGroup,
  mergeClasses,
} from "@fluentui/react-components";
import { DEMOGRAPHIC_QUESTIONS } from "apps/diagnostics/common/demographicQuestions";
import { DemographicsPlacement } from "apps/diagnostics/models/IDiagnostic";
import { IDemographicsTabProps } from "./DemographicsTab.types";
import { useStyles } from "./DemographicsTab.styles";

const QUESTION_TEXT_MAX_LENGTH = 300;

export const DemographicsTab = ({
  selectedQuestions,
  onChange,
  placement,
  onPlacementChange,
  overrides,
  onOverrideChange,
  disabled,
}: IDemographicsTabProps): JSX.Element => {
  const styles = useStyles();

  const handleToggle = (id: string, checked: boolean): void => {
    if (checked) {
      if (!selectedQuestions.includes(id)) {
        const next = [...selectedQuestions, id];
        const orderOf = (q: string): number =>
          DEMOGRAPHIC_QUESTIONS.findIndex((dq) => dq.id === q);
        next.sort((a, b) => orderOf(a) - orderOf(b));
        onChange(next);
      }
    } else {
      onChange(selectedQuestions.filter((q) => q !== id));
    }
  };

  const handleOverrideChange = (
    id: string,
    value: string,
    defaultLabel: string,
  ): void => {
    const next = { ...overrides };

    if (value.trim() === defaultLabel) {
      delete next[id];
    } else {
      next[id] = value;
    }
    onOverrideChange(next);
  };

  return (
    <div className={styles.root}>
      <p className={styles.description}>
        Select the questions to ask your interviewees. Selected questions are
        asked during the AI Interview. Edit the wording to tailor each question
        to the client, and choose where they should appear.
      </p>
      <div className={styles.placementSection}>
        <Label id="placement-label" className={styles.placementLabel}>
          Placement
        </Label>
        <RadioGroup
          value={placement}
          onChange={(_, data) =>
            onPlacementChange(data.value as DemographicsPlacement)
          }
          aria-labelledby="placement-label"
          disabled={disabled}
        >
          <Radio value="beginning" label="Beginning of the interview" />
          <Radio
            value="end"
            label="End of the interview (after all objective questions)"
          />
        </RadioGroup>
      </div>
      <div
        className={styles.checkboxGroup}
        role="group"
        aria-label="Demographic questions to include"
      >
        {DEMOGRAPHIC_QUESTIONS.map((q) => {
          const isSelected = selectedQuestions.includes(q.id);
          const value = overrides[q.id] ?? q.label;
          const isEmpty = isSelected && value.trim() === "";
          const inputId = `demographic-question-input-${q.id}`;
          return (
            <div
              key={q.id}
              className={styles.questionRow}
              role="group"
              aria-label={q.label}
            >
              <Checkbox
                label={isSelected ? undefined : value}
                checked={isSelected}
                disabled={disabled}
                aria-label={isSelected ? q.label : undefined}
                aria-controls={isSelected ? inputId : undefined}
                onChange={(_, data) => handleToggle(q.id, !!data.checked)}
              />
              {isSelected && (
                <Input
                  id={inputId}
                  className={mergeClasses(
                    styles.questionInput,
                    isEmpty && styles.questionInputError,
                  )}
                  value={value}
                  aria-label={`Question text for "${q.label}"`}
                  aria-invalid={isEmpty || undefined}
                  placeholder={q.label}
                  maxLength={QUESTION_TEXT_MAX_LENGTH}
                  disabled={disabled}
                  onChange={(_, data) =>
                    handleOverrideChange(q.id, data.value, q.label)
                  }
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
